import { useEffect, useState, useMemo } from "react";
import moment from "moment";
import { createReservation, updateReservation, getActiveCategories, createBulkReservations } from "../../api/courts";
import { ICourtReservation, ReservationStatus, IReservationCategory } from "../../models/ICourtReservation";
import { ICourt } from "../../models/ICourt";
import { ITournamentItem } from "../../models/ITournament";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { hasAdminAccess, isSuperAdmin } from "../../utils/authorization";
import { Alert, Button, DatePicker, Checkbox, DescriptionList, Dialog, Field, FormGrid, Input, Select, Textarea } from "../../ui";
import "./styles.scss";

type ReservationDialogProps = {
  reservation?: ICourtReservation;
  courts: ICourt[];
  categories: IReservationCategory[];
  tournaments?: ITournamentItem[];
  isOpen: boolean;
  onClose: (update?: boolean) => void;
  initialCourtId?: string;
  initialDate?: Date;
  initialStartTime?: string; // Format: "HH:mm"
};

const ReservationDialog = (props: ReservationDialogProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const isAdmin = hasAdminAccess(currentUser);
  const isSuperAdminUser = isSuperAdmin(currentUser);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    courtId: "",
    date: null as Date | null,
    startTime: "", // Format: "HH:mm" (e.g., "14:30")
    endTime: "", // Format: "HH:mm" (e.g., "01:00")
    status: ReservationStatus.Pending,
    reservationCategoryId: "" as string,
    tournamentId: "" as string,
    needBall: false,
    notes: "",
  });

  // Recurring (create-only, admin + tournament-linked reservations): repeat the
  // same slot weekly so admins don't have to re-enter a tournament block each week.
  const [isRecurring, setIsRecurring] = useState(false);
  const [numberOfWeeks, setNumberOfWeeks] = useState(4);

  // Filter tournaments to those whose date range includes the selected date.
  // If no date is picked yet, fall back to the full list so the field is usable.
  const availableTournaments = useMemo(() => {
    const all = props.tournaments || [];
    if (!formData.date) return all;
    const day = moment(formData.date).startOf("day");
    return all.filter((t) => {
      const s = moment(t.startDate).startOf("day");
      const e = moment(t.endDate).endOf("day");
      return day.isSameOrAfter(s) && day.isSameOrBefore(e);
    });
  }, [props.tournaments, formData.date]);

  // If the currently-selected tournament isn't in the filtered list, clear it.
  useEffect(() => {
    if (!formData.tournamentId) return;
    if (!availableTournaments.some((t) => t.id === formData.tournamentId)) {
      setFormData((prev) => ({ ...prev, tournamentId: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTournaments]);

  // Generate time options (30-minute intervals from 7:00 AM to 1:00 AM next day)
  const timeOptions = useMemo(() => {
    const options: string[] = [];
    // From 7:00 AM to 11:30 PM
    for (let hour = 7; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeStr = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
        options.push(timeStr);
      }
    }
    // From 12:00 AM to 1:00 AM
    for (let hour = 0; hour <= 1; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeStr = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
        options.push(timeStr);
      }
    }
    return options;
  }, []);

  useEffect(() => {
    if (props.isOpen) {
      setErrorMessage("");

      // Recurrence only applies to new reservations — reset on open.
      setIsRecurring(false);
      setNumberOfWeeks(4);

      if (props.reservation) {
        const startMoment = moment.utc(props.reservation.startTime).local();
        const endMoment = moment.utc(props.reservation.endTime).local();

        setFormData({
          courtId: props.reservation.courtId,
          date: startMoment.toDate(),
          startTime: startMoment.format("HH:mm"),
          endTime: endMoment.format("HH:mm"),
          status: props.reservation.status,
          reservationCategoryId: props.reservation.reservationCategoryId || "",
          tournamentId: props.reservation.tournamentId || "",
          needBall: props.reservation.needBall ?? false,
          notes: props.reservation.notes || "",
        });
      } else {
        // For new reservations, use initial values if provided, otherwise default to today
        const initialDate = props.initialDate ? moment(props.initialDate).startOf("day") : moment().startOf("day");
        setFormData({
          courtId: props.initialCourtId || "",
          date: initialDate.toDate(),
          startTime: props.initialStartTime || "",
          endTime: "",
          status: isAdmin ? ReservationStatus.Confirmed : ReservationStatus.Pending,
          reservationCategoryId: "",
          tournamentId: "",
          needBall: false,
          notes: "",
        });
      }
    }
  }, [props.isOpen, props.reservation, props.courts, props.initialCourtId, props.initialDate, props.initialStartTime]);

  const handleClose = (update?: boolean) => {
    props.onClose(update);
    setErrorMessage("");
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // If start time changes, clear end time if it's no longer in valid options
      if (field === "startTime" && updated.endTime) {
        const startIndex = timeOptions.findIndex((t) => t === updated.startTime);
        const endIndex = timeOptions.findIndex((t) => t === updated.endTime);

        if (startIndex !== -1 && endIndex !== -1 && endIndex < startIndex) {
          updated.endTime = "";
        }
      }

      return updated;
    });
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.courtId) {
      setErrorMessage("Please select a court");
      return;
    }

    // Prevent creating new reservations with inactive courts
    if (!props.reservation) {
      const selectedCourt = props.courts.find((c) => c.id === formData.courtId);
      if (selectedCourt && !selectedCourt.isActive) {
        setErrorMessage("Cannot create reservation for inactive court");
        return;
      }
    }

    if (!formData.date) {
      setErrorMessage("Please select a date");
      return;
    }

    if (!formData.startTime) {
      setErrorMessage("Please select a start time");
      return;
    }

    if (!formData.endTime) {
      setErrorMessage("Please select an end time");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      // Combine date and times
      const [startHour, startMin] = formData.startTime.split(":").map(Number);
      const [endHour, endMin] = formData.endTime.split(":").map(Number);

      // Create start datetime
      const startMoment = moment(formData.date).hour(startHour).minute(startMin).second(0).millisecond(0);

      // Create end datetime - smart handling for next day
      let endMoment = moment(formData.date).hour(endHour).minute(endMin).second(0).millisecond(0);

      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      if (endMinutes <= startMinutes) {
        endMoment = endMoment.add(1, "day");
      }

      // Validate that end time is after start time
      if (endMoment.isSameOrBefore(startMoment)) {
        setErrorMessage("End time must be after start time");
        setLoading(false);
        return;
      }

      if (props.reservation) {
        // Update existing reservation
        const updateData: any = {
          notes: formData.notes || undefined,
        };

        // Only update time if changed
        updateData.startTime = startMoment.toISOString();
        updateData.endTime = endMoment.toISOString();

        // Only update status if changed and (reservation is pending OR user is admin)
        if (
          (props.reservation.status === ReservationStatus.Pending || isAdmin) &&
          formData.status !== props.reservation.status
        ) {
          updateData.status = formData.status;
        }

        // Only update category if changed
        const currentCategoryId = props.reservation.reservationCategoryId || "";
        if (formData.reservationCategoryId !== currentCategoryId) {
          updateData.reservationCategoryId = formData.reservationCategoryId || undefined;
        }

        // Only update tournament if changed (admin-only field)
        const currentTournamentId = props.reservation.tournamentId || "";
        if (isAdmin && formData.tournamentId !== currentTournamentId) {
          updateData.tournamentId = formData.tournamentId || undefined;
        }

        // Only update needBall if changed
        if (formData.needBall !== (props.reservation.needBall ?? false)) {
          updateData.needBall = formData.needBall;
        }

        const response = await updateReservation(props.reservation.id, updateData);

        // Check if response indicates failure (success: false)
        if (response && (response as any).success === false) {
          const errorMessage = (response as any).message || "Failed to update reservation";
          setErrorMessage(errorMessage);
          setLoading(false);
          return;
        }

        dispatch(setGeneralAlertMessage({ message: "Reservation updated successfully", type: "success" }));
      } else {
        // Create new reservation
        const createData = {
          courtId: formData.courtId,
          startTime: startMoment.toISOString(),
          endTime: endMoment.toISOString(),
          notes: formData.notes || undefined,
          reservationCategoryId: formData.reservationCategoryId || undefined,
          tournamentId: isAdmin && formData.tournamentId ? formData.tournamentId : undefined,
          status: formData.status !== ReservationStatus.Pending ? formData.status : undefined,
          needBall: formData.needBall || undefined,
        };

        // Recurring: admins can duplicate the same slot for N weeks via the bulk
        // endpoint so they don't have to re-enter a tournament block each week.
        const recurEnabled = isAdmin && isRecurring && formData.tournamentId && numberOfWeeks > 1;

        if (recurEnabled) {
          const reservations = Array.from({ length: numberOfWeeks }, (_, i) => ({
            ...createData,
            startTime: startMoment.clone().add(i * 7, "days").toISOString(),
            endTime: endMoment.clone().add(i * 7, "days").toISOString(),
          }));

          const bulkRes: any = await createBulkReservations({ reservations });

          if (bulkRes && bulkRes.success === false) {
            setErrorMessage(bulkRes.message || "Failed to create recurring reservations");
            setLoading(false);
            return;
          }

          const created = Array.isArray(bulkRes?.data) ? bulkRes.data.length : numberOfWeeks;
          const partial = bulkRes?.message && created < numberOfWeeks;
          dispatch(setGeneralAlertMessage({
            message: partial
              ? `${created} of ${numberOfWeeks} recurring reservations created. ${bulkRes.message}`
              : `${created} recurring reservations created successfully`,
            type: "success",
          }));
        } else {
          const response = await createReservation(createData);

          // Check if response indicates failure (success: false)
          if (response && (response as any).success === false) {
            const errorMessage = (response as any).message || "Failed to create reservation";
            setErrorMessage(errorMessage);
            setLoading(false);
            return;
          }

          dispatch(setGeneralAlertMessage({ message: "Reservation created successfully", type: "success" }));
        }
      }
      handleClose(true);
    } catch (error: any) {
      // Extract error message from various possible structures
      let errorMessage = "An error occurred";

      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (Array.isArray(error?.response?.data?.errors) && error.response.data.errors.length > 0) {
        errorMessage = error.response.data.errors.join(", ");
      } else if (typeof error?.response?.data === "string") {
        errorMessage = error.response.data;
      }

      setErrorMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Filter end time options to start from the selected start time
  const endTimeOptions = useMemo(() => {
    if (!formData.startTime) {
      return [];
    }

    const startIndex = timeOptions.findIndex((t) => t === formData.startTime);

    if (startIndex === -1) {
      return timeOptions;
    }

    return timeOptions.slice(startIndex);
  }, [formData.startTime, timeOptions]);


  const isEditMode = !!props.reservation;
  // Allow status editing if reservation is pending OR if user is admin, or for new reservations by superadmin
  const canEditStatus = isEditMode
    ? (props.reservation?.status === ReservationStatus.Pending || isAdmin)
    : isSuperAdminUser;

  // For new reservations, only allow active courts. For editing, allow all courts (including the one already selected)
  const availableCourts = isEditMode
    ? props.courts
    : props.courts.filter((court) => court.isActive || court.id === formData.courtId);

  // Sort courts by name
  const sortedCourts = [...availableCourts].sort((a, b) => a.name.localeCompare(b.name));

  const inactiveCourtSelected =
    !isEditMode && !!formData.courtId && !props.courts.find((c) => c.id === formData.courtId)?.isActive;

  const endsNextDay = (() => {
    if (!formData.startTime || !formData.endTime) return false;
    const [startHour, startMin] = formData.startTime.split(":").map(Number);
    const [endHour, endMin] = formData.endTime.split(":").map(Number);
    return endHour * 60 + endMin <= startHour * 60 + startMin;
  })();

  const guest = props.reservation;

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      placement="side"
      title={isEditMode ? "Edit Reservation" : "Create Reservation"}
      busy={loading}
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {isEditMode ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      <div className="rsv-form">
        {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

        <Field label="Court" required error={inactiveCourtSelected ? "Cannot create reservation for inactive court" : undefined}>
          {(id) => (
            <Select
              id={id}
              block
              value={formData.courtId}
              onChange={(e) => handleChange("courtId", e.target.value)}
              disabled={loading || isEditMode}
              invalid={inactiveCourtSelected}
            >
              <option value="">Select a court</option>
              {sortedCourts.map((court) => {
                const isDisabled = !isEditMode && !court.isActive;
                return (
                  <option key={court.id} value={court.id} disabled={isDisabled}>
                    {court.name} {!court.isActive ? "(Inactive)" : ""}
                  </option>
                );
              })}
            </Select>
          )}
        </Field>

        <Field label="Category">
          {(id) => (
            <Select
              id={id}
              block
              value={formData.reservationCategoryId}
              onChange={(e) => handleChange("reservationCategoryId", e.target.value)}
              disabled={loading}
            >
              <option value="">No category</option>
              {props.categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name} (EGP {cat.pricePerHour}/hr)
                </option>
              ))}
            </Select>
          )}
        </Field>

        {isAdmin && props.tournaments && props.tournaments.length > 0 && (
          <Field
            label={
              <>
                Tournament <span className="rsv-form__optional">(optional)</span>
              </>
            }
          >
            {(id) => (
              <Select
                id={id}
                block
                value={formData.tournamentId}
                onChange={(e) => handleChange("tournamentId", e.target.value)}
                disabled={loading || !formData.date}
              >
                <option value="">
                  {!formData.date
                    ? "Select a date first"
                    : availableTournaments.length === 0
                    ? "No tournaments on this date"
                    : "No tournament"}
                </option>
                {availableTournaments.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} · {moment(t.startDate).format("DD MMM")} – {moment(t.endDate).format("DD MMM YYYY")}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        {isAdmin && !props.reservation && formData.tournamentId && (
          <div className="rsv-recurring">
            <Checkbox
              checked={isRecurring}
              onChange={(checked) => setIsRecurring(checked)}
              disabled={loading}
              label="Repeat weekly for this tournament"
            />
            {isRecurring && formData.date && (
              <div className="rsv-recurring__weeks">
                <label className="rsv-recurring__label" htmlFor="rsv-number-of-weeks">
                  Number of weeks
                </label>
                <Input
                  id="rsv-number-of-weeks"
                  type="number"
                  inputSize="sm"
                  block={false}
                  className="rsv-recurring__input"
                  min={2}
                  max={52}
                  value={numberOfWeeks}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!isNaN(v)) setNumberOfWeeks(Math.max(2, Math.min(52, v)));
                  }}
                  disabled={loading}
                />
                <span className="rsv-recurring__through">
                  through {moment(formData.date).add((numberOfWeeks - 1) * 7, "days").format("DD MMM YYYY")}
                </span>
              </div>
            )}
          </div>
        )}

        <Field label="Date" required>
          {(id) => (
            <DatePicker
              id={id}
              selected={formData.date}
              onChange={(date) => handleChange("date", date)}
              dateFormat="MMM dd, yyyy"
              placeholderText="Select date"
              disabled={loading}
              minDate={new Date()}
            />
          )}
        </Field>

        <FormGrid columns={2}>
          <Field label="Start Time" required>
            {(id) => (
              <Select id={id} block value={formData.startTime} onChange={(e) => handleChange("startTime", e.target.value)} disabled={loading}>
                <option value="">Select start time</option>
                {timeOptions.map((time) => {
                  const [hours, minutes] = time.split(":").map(Number);
                  const date = new Date();
                  date.setHours(hours, minutes, 0, 0);
                  const displayTime = moment(date).format("hh:mm A");
                  return (
                    <option key={time} value={time}>
                      {displayTime}
                    </option>
                  );
                })}
              </Select>
            )}
          </Field>

          <Field label="End Time" required hint={endsNextDay ? "End time will be on the next day" : undefined}>
            {(id) => (
              <Select
                id={id}
                block
                value={formData.endTime}
                onChange={(e) => handleChange("endTime", e.target.value)}
                disabled={loading || !formData.startTime}
              >
                <option value="">{formData.startTime ? "Select end time" : "Select start time first"}</option>
                {endTimeOptions.map((time) => {
                  const [hours, minutes] = time.split(":").map(Number);
                  const date = new Date();
                  date.setHours(hours, minutes, 0, 0);
                  const displayTime = moment(date).format("hh:mm A");
                  // Show indicator if this would be next day
                  const [startHour, startMin] = formData.startTime ? formData.startTime.split(":").map(Number) : [0, 0];
                  const startMinutes = startHour * 60 + startMin;
                  const endMinutes = hours * 60 + minutes;
                  const isNextDay = formData.startTime && endMinutes <= startMinutes;
                  return (
                    <option key={time} value={time}>
                      {displayTime} {isNextDay ? "(next day)" : ""}
                    </option>
                  );
                })}
              </Select>
            )}
          </Field>
        </FormGrid>

        {canEditStatus && (
          <Field label="Status">
            {(id) => (
              <Select id={id} block value={formData.status} onChange={(e) => handleChange("status", parseInt(e.target.value))} disabled={loading}>
                <option value={ReservationStatus.Pending}>Pending</option>
                <option value={ReservationStatus.Confirmed}>Confirmed</option>
                <option value={ReservationStatus.Cancelled}>Cancelled</option>
              </Select>
            )}
          </Field>
        )}

        {isEditMode && guest && (guest.guestName || guest.isGuest) && (
          <div className="rsv-form__section">
            <p className="rsv-form__section-title">Guest Information</p>
            <div className="rsv-guest">
              <DescriptionList
                columns={1}
                items={[
                  ...(guest.guestName ? [{ label: "Name", value: guest.guestName }] : []),
                  ...(guest.guestEmail
                    ? [{ label: "Email", value: <a href={`mailto:${guest.guestEmail}`}>{guest.guestEmail}</a> }]
                    : []),
                  ...(guest.guestPhone
                    ? [
                        {
                          label: "Phone",
                          value: (
                            <a
                              href={`https://wa.me/${guest.guestPhone.replace(/[^0-9+]/g, "").replace(/^\+/, "")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {guest.guestPhone}
                            </a>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          </div>
        )}

        <Checkbox
          checked={formData.needBall}
          onChange={(checked) => handleChange("needBall", checked)}
          disabled={loading}
          label="Need Ball (+50 EGP)"
        />

        {isEditMode && props.reservation && props.reservation.cost > 0 && (
          <Field label="Cost">
            <div className="rsv-readonly">EGP {props.reservation.cost.toLocaleString()}</div>
          </Field>
        )}

        <Field label="Notes">
          {(id) => (
            <Textarea
              id={id}
              placeholder="Additional notes (optional)"
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              rows={3}
              disabled={loading}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
};

export default ReservationDialog;
